/**
 * CLIENT CONFIGURATION
 * ====================
 * Walton Fire Protection District
 */

// ============================================
// BRANDING
// ============================================
export const BRANDING = {
  // Organization Identity
  companyName: 'Walton Fire Protection',
  companyFullName: 'Walton Fire Protection District',

  // App Identity
  appName: 'NarcoticTrack',
  appTagline: 'the most efficient and safe way to inventory controlled substances in EMS.',
  copyright: 'NarcoticTrack Inventory System, LLC',
  copyrightYear: '2025',

  // Theme Colors (white/black with gold accent)
  primaryColor: '#C5A02E',        // Gold - main buttons, accents
  secondaryColor: '#f5f5f5',      // Light gray
  backgroundColor: '#ffffff',     // White background
  cardBackground: '#ffffff',      // White cards
  textColor: '#333333',           // Dark text
  textMuted: '#666666',           // Muted text
  borderColor: '#e0e0e0',         // Light borders

  // PWA Manifest
  pwaShortName: 'Walton NT',
  pwaFullName: 'Walton Fire Protection NarcoticTrack',
  themeColor: '#C5A02E',

  // Logos (filenames in /src/assets/images/)
  logoFilename: 'WaltonLogo.png',
  ntLogoFilename: 'NTlogo.jpeg',
};

// ============================================
// SUPABASE CONFIGURATION
// ============================================
export const SUPABASE = {
  url: 'https://akdzuorzpgmyuugtktkk.supabase.co',
  anonKey: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFrZHp1b3J6cGdteXV1Z3RrdGtrIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Njc1NDU5OTYsImV4cCI6MjA4MzEyMTk5Nn0.FZTbXtJ-hRg97litBHyBTN61EW4XOcnhCaos7p19De4',
};

// ============================================
// LOCATIONS
// ============================================
export const LOCATIONS = [
  // Squads (have full seal tracking - safe + bag)
  { id: 's-91', name: 'S-91', type: 'squad', tableName: 's_91' },
  { id: 's-92', name: 'S-92', type: 'squad', tableName: 's_92' },
  { id: 's-93', name: 'S-93', type: 'squad', tableName: 's_93' },
  { id: 's-94', name: 'S-94', type: 'squad', tableName: 's_94' },

  // Engines (single seal tracking)
  { id: 'e-91', name: 'E-91', type: 'engine', tableName: 'e_91' },
  { id: 'e-92', name: 'E-92', type: 'engine', tableName: 'e_92' },

  // Ladder (single seal tracking like engines)
  { id: 'l-91', name: 'L-91', type: 'ladder', tableName: 'l_91' },

  // Station Storage (no seal tracking)
  { id: 'station-1-mezzanine', name: 'STATION 1 MEZZANINE', type: 'station', tableName: 'station_1_mezzanine' },
  { id: 'station-1-supply', name: 'STATION 1 SUPPLY', type: 'station', tableName: 'station_1_supply' },
  { id: 'station-2-mezzanine', name: 'STATION 2 MEZZANINE', type: 'station', tableName: 'station_2_mezzanine' },
  { id: 'station-2-supply', name: 'STATION 2 SUPPLY', type: 'station', tableName: 'station_2_supply' },
];

// Helper: Get locations by type
export const getLocationsByType = (type) => LOCATIONS.filter(loc => loc.type === type);
export const getLocationById = (id) => LOCATIONS.find(loc => loc.id === id);

// ============================================
// MEDICATIONS
// ============================================
export const MEDICATIONS = [
  { id: 'fentanyl_100mcg', displayName: 'Fentanyl 100mcg', schedule: 'II' },
  { id: 'ketamine_20mg', displayName: 'Ketamine 20mg', schedule: 'III' },
  { id: 'ketamine_500mg', displayName: 'Ketamine 500mg/5mL', schedule: 'III' },
  { id: 'morphine_10mg', displayName: 'Morphine 10mg', schedule: 'II' },
  { id: 'midazolam_5mg', displayName: 'Midazolam 5mg', schedule: 'IV' },
];

// Get medication IDs as array (for DB queries)
export const MEDICATION_IDS = MEDICATIONS.map(m => m.id);

// Format medication name for display
export const formatMedicationName = (id) => {
  const med = MEDICATIONS.find(m => m.id === id);
  return med ? med.displayName : id;
};

// Get medications for a specific location
export const getLocationMedications = (locationId) => {
  return {
    sections: [
      {
        title: null,
        medications: MEDICATIONS
      }
    ]
  };
};

// ============================================
// SEAL TRACKING
// ============================================
export const SEAL_CONFIG = {
  // Squads have full seal tracking (safe + bag)
  squad: {
    enabled: true,
    fields: [
      { id: 'safe_seal_number', displayName: 'Safe Seal Number', required: true },
      { id: 'new_safe_seal_number', displayName: 'New Safe Seal Number', required: false, hint: '(If Applicable)' },
      { id: 'bag_seal_number', displayName: 'Bag Seal Number', required: true },
      { id: 'new_bag_seal_number', displayName: 'New Bag Seal Number', required: false, hint: '(If Applicable)' },
    ],
  },
  // Engines have single seal tracking
  engine: {
    enabled: true,
    fields: [
      { id: 'seal_number', displayName: 'Seal Number', required: true },
      { id: 'new_seal_number', displayName: 'New Seal Number', required: false, hint: '(If Applicable)' },
    ],
  },
  // Ladder has single seal tracking (same as engine)
  ladder: {
    enabled: true,
    fields: [
      { id: 'seal_number', displayName: 'Seal Number', required: true },
      { id: 'new_seal_number', displayName: 'New Seal Number', required: false, hint: '(If Applicable)' },
    ],
  },
  // Stations have no seal tracking
  station: { enabled: false, fields: [] },
};

// Get seal fields for a location type
export const getSealFields = (locationType) => {
  return SEAL_CONFIG[locationType]?.fields || [];
};

// ============================================
// FORM REQUIREMENTS
// ============================================
export const FORM_CONFIG = {
  // Signature Requirements
  requirePrimarySignature: true,
  requireSecondarySignature: true,
  requireSecondaryPinValidation: true,

  // Field Requirements
  requireInventoryPhoto: false,
  requireRunNumber: false,
  requireReasonForChange: true,
  requireAdditionalNotes: false,

  // Field Visibility
  showRunNumber: true,
  showAdditionalNotes: true,
  showInventoryPhoto: true,

  // PIN validation rules
  pinMinLength: 6,
  pinMaxLength: 6,

  // Session timeout (milliseconds)
  sessionTimeout: 5 * 60 * 1000,

  // Reason for change options
  reasonOptions: [
    'Daily Inventory',
    'Restock',
    'Used on Patient',
    'Expired Removal',
    'Damaged/Wasted',
    'Transfer',
    'Other'
  ],
};

// ============================================
// FEATURE FLAGS
// ============================================
export const FEATURES = {
  // Dashboards
  dailyDashboard: true,
  adminDashboard: true,

  // Reports (Admin Only)
  chainOfCustodyReport: true,
  providerUsageReport: true,
  masterInventory: false,
  expirationReport: false,
  mileageReport: false,

  // Medical Calculators
  patientCareGuidelines: false,
  weightConverter: false,
  ivCalculator: false,
  glasgowComaScale: false,
  apgarCalculator: false,
  oxygenDuration: false,
  phoneDirectory: false,

  // Medication Tracking
  expirationTracking: false,
  expirationAlertDays: 30,

  // DEA Compliance
  deaForm41: true,
};

// ============================================
// ADMIN FUNCTIONS
// ============================================
export const ADMIN_FEATURES = [
  { id: 'users', name: 'Manage Users', path: '/users', enabled: true },
  { id: 'chain-of-custody', name: 'Chain Of Custody Report', path: '/chain-of-custody-report', enabled: true },
  { id: 'provider-usage', name: 'Provider Usage Report', path: '/provider-usage-report', enabled: true },
  { id: 'inventory-report', name: 'Inventory Report', path: '/inventory-report', enabled: true },
];

// ============================================
// LOCATION IDENTIFIER
// ============================================
export const CLIENT_ID = 'walton';

// ============================================
// EXPORT ALL CONFIG
// ============================================
const CONFIG = {
  BRANDING,
  SUPABASE,
  LOCATIONS,
  MEDICATIONS,
  MEDICATION_IDS,
  SEAL_CONFIG,
  FORM_CONFIG,
  FEATURES,
  ADMIN_FEATURES,
  CLIENT_ID,

  // Helper functions
  getLocationsByType,
  getLocationById,
  formatMedicationName,
  getLocationMedications,
  getSealFields,
};

export default CONFIG;
